/* global BigInt */
import React, { useEffect, useState } from 'react';
import image from '../../images/lili.png'
import { useDispatch } from 'react-redux';
import { setModalStage } from '../../store/modalSlice';
import Button from '../../share/button/Button';
import { useAccount, useReadContract, useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import { formatEther } from 'viem';
import { LILVERSE_MINT_ADDRESS, LILVERSE_MINT_ABI, fetchOgProofs } from '../../web3/lilverseMintContract';
import { useGasFees, feeOverrides } from '../../web3/useGasFees';
import NetworkFee from '../../share/networkfee/NetworkFee';

const MAX_QUANTITY = 5;

const OgMint = () => {
    const dispatch = useDispatch();
    const { address } = useAccount();
    // Defaults to the full OG allowance on purpose: the contract records the OG
    // claim per wallet on the FIRST successful mintOg call regardless of quantity,
    // so anything less than MAX_QUANTITY permanently forfeits the remainder at the
    // OG price. There is no second OG mint and no way to reset it.
    const [quantity, setQuantity] = useState(MAX_QUANTITY);
    const [proof, setProof] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);

    useEffect(() => {
        if (!address) return;
        fetchOgProofs().then((data) => setProof(data.proofs[address] || null));
    }, [address]);

    const { data: totalWei, isLoading: priceLoading, error: totalWeiError, refetch: refetchTotal } = useReadContract({
        address: LILVERSE_MINT_ADDRESS,
        abi: LILVERSE_MINT_ABI,
        functionName: 'quoteOgTotalWei',
        args: [BigInt(quantity)],
    });

    const { data: perTokenWei, error: perTokenError, refetch: refetchPerToken } = useReadContract({
        address: LILVERSE_MINT_ADDRESS,
        abi: LILVERSE_MINT_ABI,
        functionName: 'quoteOgPriceWei',
        args: [1n],
    });

    const priceError = totalWeiError || perTokenError;

    const gasFees = useGasFees();

    const { writeContractAsync, isPending: isMinting } = useWriteContract();
    const [txHash, setTxHash] = useState(null);
    const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({ hash: txHash });

    useEffect(() => {
        if (isSuccess) dispatch(setModalStage('FINISH_X'));
    }, [isSuccess, dispatch]);

    const decrementNumber = () => setQuantity((n) => Math.max(1, n - 1));
    const incrementNumber = () => setQuantity((n) => Math.min(MAX_QUANTITY, n + 1));

    const handleMint = async () => {
        setErrorMessage(null);
        if (!proof || totalWei === undefined || perTokenWei === undefined) return;
        try {
            // 3% slippage buffer on both the per-token bound and the value sent --
            // the contract computes the live oracle price at execution time, which
            // can drift slightly from this quote; any excess is auto-refunded.
            const maxWeiPerToken = (perTokenWei * 103n) / 100n;
            const value = (totalWei * 103n) / 100n;
            const hash = await writeContractAsync({
                address: LILVERSE_MINT_ADDRESS,
                abi: LILVERSE_MINT_ABI,
                functionName: 'mintOg',
                args: [BigInt(quantity), maxWeiPerToken, proof],
                value,
                // See PublicMint: zero-tip transactions stall on full blocks.
                ...feeOverrides(gasFees),
            });
            setTxHash(hash);
        } catch (err) {
            setErrorMessage(err?.shortMessage || err?.message || 'Mint transaction failed.');
        }
    };

    const busy = isMinting || isConfirming;

    return (
        <div className="flex flex-col items-center gap-2 mt-4 sm:mt-0">
            <img src={image} className="w-[110px] sm:w-[140px] h-auto mb-1" alt="Lili" />
            <h2 className="text-white uppercase text-[clamp(1.75rem,6vw,3.5rem)] leading-tight text-center">OG mint</h2>
            <p className="font-dang text-lg sm:text-xl md:text-[1.7rem] text-white text-center -tracking-[1px] mt-2">You can mint up to {MAX_QUANTITY} Lilverse NFTs</p>
            <div className="flex gap-4 sm:gap-8 items-center mb-2 mt-4">
                <Button onClick={decrementNumber} disabled={busy} size="counter" className="font-dang">-</Button>
                <Button size="counter" className="font-dang">{quantity}</Button>
                <Button onClick={incrementNumber} disabled={busy} size="counter" className="font-dang">+</Button>
            </div>
            {quantity < MAX_QUANTITY && (
                <p className="font-mons text-[13px] sm:text-sm text-white bg-[#B8355F] rounded-xl px-4 py-3 mb-3 text-center max-w-[430px] leading-snug">
                    <b>This is your only OG mint.</b> You can claim the OG price once &mdash; if you mint {quantity}, the other {MAX_QUANTITY - quantity} will only be available at the full public price.
                </p>
            )}
            <p className="font-dang text-lg sm:text-xl text-white text-center -tracking-[1px] mb-4">
                {priceError ? 'Could not load price' : (priceLoading || totalWei === undefined) ? 'Loading price…' : `Total: ${formatEther(totalWei)} ETH`}
            </p>
            {priceError && (
                <div className="flex flex-col items-center gap-2 mb-3">
                    <p role="alert" className="font-mons text-sm text-white bg-[#D14D4D] rounded-lg px-4 py-2 text-center max-w-[400px]">
                        {priceError.shortMessage || priceError.message || 'Price feed read failed.'}
                    </p>
                    <button
                        onClick={() => { refetchTotal(); refetchPerToken(); }}
                        className="font-mons text-sm text-white underline"
                    >
                        Retry
                    </button>
                </div>
            )}
            {errorMessage && (
                <p role="alert" className="font-mons text-sm text-white bg-[#D14D4D] rounded-lg px-4 py-2 mb-3 text-center max-w-[400px]">
                    {errorMessage}
                </p>
            )}
            <NetworkFee fees={gasFees} quantity={quantity} />
            <Button
                size="cta"
                onClick={handleMint}
                disabled={busy || !proof || totalWei === undefined || Boolean(priceError)}
            >
                {isMinting ? 'Confirm in wallet…' : isConfirming ? 'Minting…' : `Mint ${quantity} NFT${quantity > 1 ? 's' : ''}`}
            </Button>
        </div>
    );
};

export default OgMint;
